/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import IEnergyManagementProps, {
  IAny
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import useMounted from '../../shared/useMount/useMount';
import useConnectorAndDynamicProps from '../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import { isObjectOrArrayAndEmpty } from '../../shared/Utils/Utils';
import { Card, Icon, Notification } from '@scuf/common';
import withLoadingStatus from '../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../shared/StatusWrappers/ErrorStatus';
import ConnectionError from '../../shared/ErrorComponents/ConnectionError';
import { useFileUploadContext } from '../../../context/FileUploadContext/FileUploadContext';
import FileUploadAction from '../../../context/FileUploadContext/FileUploadActions';
import {
  apiNoContentStatusCode,
  UpdatedManualFileUploadTypeList,
  UpdatedTemplateDownloadList
} from '../../shared/Utils/Constants';
import UpdatedDataUploadFileTable, {
  IUploadFile
} from './UpdatedDataUploadFileTable';
import UpdatedUploadModal from '../MeterDataUploadModal/UpdatedUploadModal';
import '../MeterDataUploadModal/ChooseDataTypeSection/UpdatedChooseDataTypeThemes.scss';
import { ApiContext } from '../../../context/ApiContext';
import PublishModal from '../MeterDataUploadModal/PublishModal/PublishModal';
import _ from 'lodash';

interface IDataUploadContent {
  data: IUploadFile[];
  onRefresh: () => Promise<void>;
  statusCode: number | null;
  activeIndex: number;
  selectedYear: string;
  setSelectedYear: Function;
  pointHistoryUploadToggle?: boolean;
}

const UpdatedDataUploadContent = (props: IDataUploadContent) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, statusCode } = props;

  if (
    isObjectOrArrayAndEmpty(data) &&
    statusCode &&
    !(statusCode == apiNoContentStatusCode)
  ) {
    return (
      <ConnectionError
        title={t(d.CONNECTIONERRORTITLE)}
        message={t(d.CONNECTIONERROR)}
        onRefresh={onRefresh}
      />
    );
  }

  return (
    <div className="data-upload-tab">
      <UpdatedDataUploadFileTable
        data={data as IUploadFile[]}
        onRefresh={onRefresh}
      />
    </div>
  );
};

const UpdatedDataUploadContentWithLoader = withLoadingStatus(
  withErrorStatus(UpdatedDataUploadContent)
);

const UpdatedDataUploadComponent = (props: IEnergyManagementProps) => {
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString()
  );
  const { bearerToken, requestContext, pointHistoryUploadToggle } = props;
  const [activeIndex, SetactiveIndex] = useState(0);
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const [state, dispatch] = useFileUploadContext();
  const isMounted = useMounted();
  const {
    submitFileForChunkedUpload,
    progressTrackingList,
    onNewTemplateDownloadClick
  } = useContext(ApiContext);

  const getUploadedMeterDataFiles = {
    name: 'meter-data-upload-get-csv-files',
    options: {
      pageNumber: '1'
    }
  };

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig: getUploadedMeterDataFiles,
    requestContext,
    bearerToken
  });

  useEffect(() => {
    if (
      isMounted &&
      state.showModal !== true &&
      (_.isUndefined(progressTrackingList) ||
        progressTrackingList.length === 0 ||
        (progressTrackingList.length > 0 &&
          progressTrackingList.filter((v) => v.removed === true).length ===
            progressTrackingList.length))
    ) {
      dataConnector && dataConnector.actions && dataConnector.actions.getAll();
    }
  }, [requestContext, state.showModal, activeIndex, progressTrackingList]);

  useEffect(() => {
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  }, [selectedYear]);

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const removeSilentLoadingonRefresh = () => {
    setIsSilentLoading(false);
    return onRefresh();
  };
  const { all, loading, error, status } = dataConnector.data;

  const fileRecords = all as IUploadFile[];
  const isDataAvailable = fileRecords ? true : false;
  const [silentLoading, setIsSilentLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState<IUploadFile[]>([]);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData([...fileRecords]);
      } else {
        setData([]);
      }
      setErrorInfo(error);
    }
    setStatusCode(status);
  }, [isDataAvailable, loading, status]);

  const showModal = () => dispatch(FileUploadAction.showUploadModal());
  const showPublishModal = () => dispatch(FileUploadAction.showPublishModal());
  const getCommonHOCForDataUpload = () => {
    return (
      <>
        <Card.Content className="data-upload-content-container">
          <UpdatedDataUploadContentWithLoader
            loading={isLoading}
            loadingText={t(d.LOADING)}
            data={data}
            hideNoData={true}
            onRefresh={removeSilentLoadingonRefresh}
            title={t(d.CONNECTIONERRORTITLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            statusCode={statusCode}
            silentLoading={silentLoading}
            error={errorInfo}
            noDataMessage={''}
            activeIndex={activeIndex}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            pointHistoryUploadToggle={pointHistoryUploadToggle}
          />
        </Card.Content>
      </>
    );
  };
  const handleStartUpload = async (files: File[]) => {
    if (submitFileForChunkedUpload) {
      files.forEach((file) => {
        submitFileForChunkedUpload(
          file,
          UpdatedManualFileUploadTypeList[0].allowedFiles[0].uploadType
        );
      });
    }
  };

  const [selectedDataType, setSelectedDataTye] = useState(
    UpdatedManualFileUploadTypeList[0].type
  );
  const allowedFiles = UpdatedManualFileUploadTypeList.find(
    (r) => r.type === selectedDataType
  )?.allowedFiles[0];

  const selectedTemplateObject = UpdatedTemplateDownloadList.find(
    (x) => x.type === selectedDataType
  );

  return (
    <div className="emaas-data-upload-settings updated-meter-data-settings">
      <div className="data-upload-section">
        <div>
          <div className="data-upload-header">
            <div className="data-upload-title">{t(d.METERDATA1)}</div>
          </div>
          <div className="data-upload-content">
            <div>{t(d.METERDATASUBHEADER)}</div>
            <div className="file-upload-publish-basline-button">
              <div
                className="data-upload-right-content-new"
                onClick={showModal}
              >
                <Icon root="common" name="badge-plus" size="medium" />
                <span>{t(d.UPLOADTOPADD)}</span>
              </div>
              <div
                className="data-upload-right-content-new"
                onClick={showPublishModal}
              >
                <Icon root="common" name="cloud-upload" size="medium" />
                <span>{t(d.PUBLISHFILE)}</span>
              </div>
            </div>
          </div>
          <div className="notification-box">
            <Notification
              hasIcon={true}
              severity="information"
              className="meter-data-publish-content notification-content"
            >
              <div>{t(d.METERUPLOADINSTRUCTIONTITLE)}</div>
              <div>
                <li>
                  <span>{t(d.METERUPLOADINSTRUCTIONONE)}</span>
                  <span
                    className="template-download"
                    onClick={() =>
                      onNewTemplateDownloadClick &&
                      onNewTemplateDownloadClick(
                        selectedDataType,
                        selectedTemplateObject?.templateFiles
                          .PointHistoryReferenceSchema.downloadFile || '',
                        selectedTemplateObject?.templateFiles
                          .PointHistoryReferenceSchema.uploadType || ''
                      )
                    }
                  >
                    {t(d.METERUPLOADDOWNLOADCSV)}
                  </span>
                </li>
                <li>
                  {t(d.METERUPLOADINSTRUCTIONFOUR)}
                  <span
                    className="template-download"
                    onClick={() =>
                      onNewTemplateDownloadClick &&
                      onNewTemplateDownloadClick(
                        selectedDataType,
                        selectedTemplateObject?.templateFiles
                          .PointHistoryDataSizeReferenceSchema.downloadFile ||
                          '',
                        selectedTemplateObject?.templateFiles
                          .PointHistoryDataSizeReferenceSchema.uploadType || ''
                      )
                    }
                  >
                    {t(d.METERDATAEXCELDOWNLOAD)}
                  </span>
                </li>
                <li>{t(d.METERUPLOADINSTRUCTIONTWO)}</li>
              </div>
            </Notification>
          </div>
          <div className="emass-data-upload">
            <Card className="data-upload-boarder">
              <Card.Header className="data-upload-container">
                {getCommonHOCForDataUpload()}
              </Card.Header>
            </Card>
          </div>
        </div>
        <PublishModal
          onRefresh={onRefresh}
          isUploadInProgress={
            progressTrackingList &&
            (progressTrackingList?.length > 0 ||
              progressTrackingList?.filter(
                (v) => !v.removed || v.status === 'Uploading'
              ).length > 0)
          }
        />
        <UpdatedUploadModal startUpload={handleStartUpload} />
      </div>
    </div>
  );
};
export default UpdatedDataUploadComponent;
